import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Award,
  Minus,
  ChevronUp,
} from 'lucide-react';
import { CATEGORIES } from '../data/categories';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import type { CommunitySubmission, SubmissionAgeGroup } from '../types';

// ── Legacy community posts (hidden from UI — data preserved for migration) ──────
// The previous free-text post feed has been replaced by the structured
// scenario-submission pipeline below. The old UI is accessible at /community-legacy
// (direct URL only). See LegacyCommunityPost in this file for the archived model.
// TODO: migrate any real Cosmos DB LegacyPost documents before removing this.

// ── Mock submissions (replace with GET /api/community/submissions) ──────────────
const INITIAL_SUBMISSIONS: CommunitySubmission[] = [
  {
    id: 'cs-001',
    submittedBy: 'user-123',
    submitterName: 'Emma P.',
    submittedAt: '2024-02-10',
    title: 'What to do if your bike is stolen',
    ageGroup: '11-13',
    category: 'travel-public',
    scenarioText: "You've locked your bike outside the shop and come back to find it's gone. What do you do?",
    discussionPrompts: [
      "Who is the first person you would tell?",
      "What information would the police need from you?",
    ],
    safetyTip: "Always note down your bike's serial number so you can report it accurately.",
    tags: ['bike', 'theft', 'public places'],
    upvotes: 24,
    status: 'approved',
  },
  {
    id: 'cs-002',
    submittedBy: 'user-456',
    submitterName: 'James T.',
    submittedAt: '2024-03-01',
    title: 'A friend asks you to lie to their parents',
    ageGroup: '8-10',
    category: 'dealing-with-conflict',
    scenarioText: 'Your best friend asks you to cover for them and lie to their mum about where they were.',
    discussionPrompts: [
      "Why might this feel difficult to say no to?",
      "What could you say to your friend that is honest but still kind?",
    ],
    tags: ['honesty', 'peer pressure', 'friendship'],
    upvotes: 11,
    status: 'pending',
  },
  {
    id: 'cs-003',
    submittedBy: 'user-789',
    submitterName: 'Sian M.',
    submittedAt: '2024-03-05',
    title: 'Someone you don\'t know sends you a game friend request',
    ageGroup: '8-10',
    category: 'online-safety-digital',
    scenarioText: "You're playing an online game and someone with no profile picture sends you a friend request and asks for your name.",
    discussionPrompts: [
      "What information should you never share with strangers online?",
      "What would you do if they kept messaging you?",
    ],
    safetyTip: "Real friends online are people you already know in real life.",
    tags: ['gaming', 'online strangers', 'personal information'],
    upvotes: 37,
    status: 'approved',
  },
  {
    id: 'cs-004',
    submittedBy: 'user-101',
    submitterName: 'Dan B.',
    submittedAt: '2024-03-12',
    title: 'Being left out of a birthday party',
    ageGroup: '5-7',
    category: 'navigating-emotions',
    scenarioText: 'Everyone in your class gets invited to a party except you. How do you cope with those feelings?',
    discussionPrompts: [
      "What feelings come up when you feel left out?",
      "Who could you talk to about it?",
    ],
    tags: ['exclusion', 'feelings', 'friendship'],
    upvotes: 8,
    status: 'pending',
  },
];

const STATUS_CONFIG = {
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    className: 'bg-green-100 text-green-700',
  },
  pending: {
    label: 'Pending Review',
    icon: Clock,
    className: 'bg-amber-100 text-amber-700',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    className: 'bg-red-100 text-red-700',
  },
};

const AGE_GROUPS: { label: string; value: SubmissionAgeGroup }[] = [
  { label: '5–7 years', value: '5-7' },
  { label: '8–10 years', value: '8-10' },
  { label: '11–13 years', value: '11-13' },
  { label: '14+ years', value: '14+' },
];

interface SubmitFormState {
  title: string;
  ageGroup: SubmissionAgeGroup;
  category: string;
  scenarioText: string;
  discussionPrompts: string[];
  safetyTip: string;
  tags: string;
  submitterNote: string;
}

const DEFAULT_FORM: SubmitFormState = {
  title: '',
  ageGroup: '8-10',
  category: '',
  scenarioText: '',
  discussionPrompts: ['', ''],
  safetyTip: '',
  tags: '',
  submitterNote: '',
};

type ActiveTab = 'community' | 'my-submissions';

export default function Community() {
  const user = useAppStore((s) => s.user);
  const { accounts, currentAccountId } = useAuthStore();
  const currentAccount = accounts.find((a) => a.id === currentAccountId) ?? null;
  const isAdmin = currentAccount?.role === 'admin';

  // TODO: Replace local state with API-backed state: GET /api/community/submissions
  const [submissions, setSubmissions] =
    useState<CommunitySubmission[]>(INITIAL_SUBMISSIONS);
  const [activeTab, setActiveTab] = useState<ActiveTab>('community');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<SubmitFormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // ── Discussion prompts helpers ─────────────────────────────────────────────
  const addPrompt = () => {
    if (form.discussionPrompts.length < 4) {
      setForm((f) => ({ ...f, discussionPrompts: [...f.discussionPrompts, ''] }));
    }
  };
  const removePrompt = (idx: number) => {
    if (form.discussionPrompts.length > 2) {
      setForm((f) => ({
        ...f,
        discussionPrompts: f.discussionPrompts.filter((_, i) => i !== idx),
      }));
    }
  };
  const updatePrompt = (idx: number, value: string) => {
    setForm((f) => ({
      ...f,
      discussionPrompts: f.discussionPrompts.map((p, i) => (i === idx ? value : p)),
    }));
  };

  // TODO: Replace with POST /api/community/submissions
  const handleSubmit = async () => {
    const filledPrompts = form.discussionPrompts.filter((p) => p.trim());
    if (!form.title.trim() || !form.category || !form.scenarioText.trim() || filledPrompts.length < 2) return;

    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 600));

    const newSubmission: CommunitySubmission = {
      id: 'cs-' + Date.now(),
      submittedBy: user?.id ?? 'guest',
      submitterName: user?.name ?? 'Anonymous',
      submittedAt: new Date().toISOString(),
      title: form.title,
      ageGroup: form.ageGroup,
      category: form.category,
      scenarioText: form.scenarioText,
      discussionPrompts: filledPrompts,
      safetyTip: form.safetyTip.trim() || undefined,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      submitterNote: form.submitterNote.trim() || undefined,
      upvotes: 0,
      status: 'pending',
    };

    setSubmissions((prev) => [newSubmission, ...prev]);
    setForm(DEFAULT_FORM);
    setSubmitting(false);
    setShowModal(false);
    setSubmitSuccess(true);
    setActiveTab('my-submissions');
    setTimeout(() => setSubmitSuccess(false), 6000);
  };

  const isFormValid =
    form.title.trim() &&
    form.category &&
    form.scenarioText.trim() &&
    form.discussionPrompts.filter((p) => p.trim()).length >= 2;

  const userSubmissions = submissions.filter((s) => s.submittedBy === user?.id);

  // Admins and the submitter see their own pending/rejected; others see approved only
  const communitySubmissions = submissions.filter(
    (s) =>
      isAdmin ||
      s.status === 'approved' ||
      s.submittedBy === user?.id
  );

  const displayedSubmissions =
    activeTab === 'my-submissions' ? userSubmissions : communitySubmissions;

  return (
    <div className="px-4 pt-4 pb-24">
      {/* Header */}
      <div className="pt-4 mb-5">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-extrabold text-gray-800">
              Community
            </h1>
            <p className="text-gray-400 text-sm mt-0.5">
              Suggest scenarios for the library
            </p>
          </div>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => { setShowModal(true); setSubmitSuccess(false); }}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2.5 rounded-2xl font-bold text-sm shadow-md hover:bg-primary-700 transition-colors"
          >
            <Plus size={16} />
            Suggest
          </motion.button>
        </div>
      </div>

      {/* Submission success banner */}
      <AnimatePresence>
        {submitSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="bg-green-50 border border-green-200 rounded-2xl px-4 py-3 mb-4 flex items-start gap-3"
          >
            <CheckCircle size={18} className="text-green-600 mt-0.5 shrink-0" />
            <p className="text-green-800 text-sm leading-relaxed">
              <strong>Thanks for your suggestion!</strong> Our team will review it and, if approved, it will appear in the Scenario Library.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contributions strip */}
      <div className="bg-gradient-to-br from-primary-50 to-violet-50 border border-primary-100 rounded-3xl p-4 mb-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center">
          <Award size={22} className="text-primary-600" />
        </div>
        <div className="flex-1">
          <p className="font-display font-bold text-gray-800 text-sm">Your Contributions</p>
          <p className="text-gray-500 text-xs mt-0.5">
            {userSubmissions.length} scenario{userSubmissions.length !== 1 ? 's' : ''} submitted
          </p>
        </div>
        {userSubmissions.length >= 1 && (
          <span className="bg-primary-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
            🤝 Contributor
          </span>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex bg-gray-100 rounded-2xl p-1 mb-5">
        {([
          { id: 'community', label: 'Community', icon: <Users size={13} /> },
          { id: 'my-submissions', label: 'My Submissions', icon: <Award size={13} /> },
        ] as const).map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold transition-all ${
              activeTab === t.id
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.icon}
            {t.label}
            {t.id === 'my-submissions' && userSubmissions.length > 0 && (
              <span className="bg-primary-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-0.5">
                {userSubmissions.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Submissions list */}
      {displayedSubmissions.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-4xl mb-3">💡</p>
          <p className="font-semibold text-gray-500 text-sm">
            {activeTab === 'my-submissions'
              ? "You haven't submitted any scenarios yet"
              : 'No approved submissions yet'}
          </p>
          {activeTab === 'my-submissions' && (
            <button
              onClick={() => setShowModal(true)}
              className="mt-3 text-primary-600 text-sm font-semibold hover:underline"
            >
              Suggest your first scenario →
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayedSubmissions.map((sub, i) => {
            const statusCfg = STATUS_CONFIG[sub.status];
            const StatusIcon = statusCfg.icon;
            const cat = CATEGORIES.find((c) => c.id === sub.category || c.id === sub.categoryId);

            return (
              <motion.div
                key={sub.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl p-4 shadow-card"
              >
                {/* Meta row */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {cat && <span className="text-base">{cat.icon}</span>}
                    <span className="text-xs font-semibold text-gray-400">
                      {cat?.name ?? sub.category}
                    </span>
                    <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {sub.ageGroup}
                    </span>
                  </div>
                  <span
                    className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${statusCfg.className}`}
                  >
                    <StatusIcon size={11} />
                    {statusCfg.label}
                  </span>
                </div>

                <h3 className="font-display font-bold text-gray-800 text-sm mb-1 leading-snug">
                  {sub.title}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-2 line-clamp-2">
                  {sub.scenarioText ?? sub.scenarioDescription}
                </p>

                {/* Discussion prompts preview */}
                {sub.discussionPrompts && sub.discussionPrompts.length > 0 && (
                  <p className="text-primary-600 text-[10px] font-semibold mb-2">
                    {sub.discussionPrompts.length} discussion prompt{sub.discussionPrompts.length !== 1 ? 's' : ''}
                  </p>
                )}

                {/* Tags */}
                {sub.tags && sub.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {sub.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="bg-gray-100 text-gray-500 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between mt-1">
                  <span className="text-gray-400 text-xs">by {sub.submitterName}</span>
                  <div className="flex items-center gap-1 text-gray-400 text-xs font-semibold">
                    <ChevronUp size={13} />
                    {sub.upvotes ?? sub.votes ?? 0}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Submit Modal ─────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm"
            />

            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 28 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl px-4 pt-5 pb-8 max-h-[92vh] overflow-y-auto"
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-xl font-extrabold text-gray-800">
                  Suggest a Scenario
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-5">
                {/* Scenario Title */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Scenario Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={80}
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. What if a stranger asks for help with directions?"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-300 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                  />
                  <p className="text-gray-300 text-[10px] mt-1 text-right">{form.title.length}/80</p>
                </div>

                {/* Age Group */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Age Group <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {AGE_GROUPS.map((ag) => (
                      <button
                        key={ag.value}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, ageGroup: ag.value }))}
                        className={`py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                          form.ageGroup === ag.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-100 text-gray-500 hover:border-gray-200'
                        }`}
                      >
                        {ag.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Category <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 bg-white"
                  >
                    <option value="">Select a category…</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Scenario Description */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Scenario Description <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    maxLength={600}
                    value={form.scenarioText}
                    onChange={(e) => setForm((f) => ({ ...f, scenarioText: e.target.value }))}
                    placeholder="Describe the situation the child faces. Be specific and realistic — e.g. 'You're at the park with your friend when a stranger approaches and asks you to help find their lost puppy…'"
                    rows={4}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-300 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 resize-none"
                  />
                  <p className="text-gray-300 text-[10px] mt-1 text-right">{form.scenarioText.length}/600</p>
                </div>

                {/* Discussion Prompts */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Discussion Prompts <span className="text-red-400">*</span>
                    <span className="text-gray-400 normal-case font-normal ml-1">(2–4 questions)</span>
                  </label>
                  <div className="space-y-2">
                    {form.discussionPrompts.map((prompt, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={prompt}
                          onChange={(e) => updatePrompt(idx, e.target.value)}
                          placeholder={`Question ${idx + 1}…`}
                          className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-700 placeholder-gray-300 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                        />
                        {form.discussionPrompts.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removePrompt(idx)}
                            className="text-gray-300 hover:text-red-400 transition-colors"
                          >
                            <Minus size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    {form.discussionPrompts.length < 4 && (
                      <button
                        type="button"
                        onClick={addPrompt}
                        className="flex items-center gap-1.5 text-primary-600 text-xs font-semibold hover:text-primary-700 transition-colors"
                      >
                        <Plus size={13} /> Add another question
                      </button>
                    )}
                  </div>
                </div>

                {/* Safety Tip (optional) */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Safety Tip
                    <span className="text-gray-400 normal-case font-normal ml-1">(optional)</span>
                  </label>
                  <textarea
                    maxLength={200}
                    value={form.safetyTip}
                    onChange={(e) => setForm((f) => ({ ...f, safetyTip: e.target.value }))}
                    placeholder="Any short safety note for this scenario…"
                    rows={2}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-300 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 resize-none"
                  />
                  <p className="text-gray-300 text-[10px] mt-1 text-right">{form.safetyTip.length}/200</p>
                </div>

                {/* Tags (optional) */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Tags
                    <span className="text-gray-400 normal-case font-normal ml-1">(optional, comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                    placeholder="e.g. strangers, outdoors, peer pressure"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-300 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                  />
                </div>

                {/* Why is this valuable? (optional) */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Why is this valuable?
                    <span className="text-gray-400 normal-case font-normal ml-1">(optional)</span>
                  </label>
                  <textarea
                    maxLength={300}
                    value={form.submitterNote}
                    onChange={(e) => setForm((f) => ({ ...f, submitterNote: e.target.value }))}
                    placeholder="Help us understand why you think this scenario is important for children…"
                    rows={3}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-300 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 resize-none"
                  />
                  <p className="text-gray-300 text-[10px] mt-1 text-right">{form.submitterNote.length}/300</p>
                </div>

                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSubmit}
                  disabled={submitting || !isFormValid}
                  className="w-full bg-primary-600 text-white py-4 rounded-2xl font-bold text-base shadow-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting…' : 'Submit Scenario'}
                </motion.button>

                <p className="text-gray-400 text-xs text-center">
                  Fields marked <span className="text-red-400">*</span> are required. Minimum 2 discussion prompts.
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Legacy community post model (archived — DO NOT delete) ───────────────────
// Fields: id, submittedBy, submitterName, title, scenarioDescription,
// categoryId, ageGroup, status, votes, comments[], createdAt
// Cosmos DB container: LegacyPost (renamed from community-posts)
